import { Component, EventEmitter, ViewChild, ViewContainerRef, ComponentFactoryResolver, Type } from "@angular/core";
import { Subject, Observable } from "rxjs";

/*
    constructor(private modalDialogService: ModalDialogService) {
    }

Example:
    const modal = this.modalDialogService.createModal();
    modal.showAlert("Website Manager", "asd asda sdas as dasd asd asd asd asd asdasdasd").subscribe({
        next: (button) => modal.close(),
        complete: () => this.modalDialogService.dismissModal(modal)
    });

Example:
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Website Manager");
    modal.setBody("Test Message !!!");
    modal.setShowCloseButton(false);
    modal.setButtonOkCancel();
    modal.show().subscribe({
        next: (button) => {
            if (button.index == 0) {
                modal.close();
            }
            else if (button.index == 1) {
                modal.close();
            }
        },
        complete: () => this.modalDialogService.dismissModal(modal)
    });

Example:
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Exam Properties");
    modal.setBodyComponent(ExamPropertiesComponent);
    modal.setButtonCancelOk();
    modal.show().subscribe(
        button => {
            if (button.index == 0) {
                modal.close();
            }
            else if (button.index == 1) {
                modal.close();
            }
        },
        error => {},
        () => {
            this.modalDialogService.dismissModal(modal);
        },
    )
*/

export const enum MODALDIALOGSIZE {
  Small,
  Normal,
  Large,
}

@Component({
  selector: "hkt-modaldialog",
  templateUrl: "./modaldialog.component.html",
  styleUrls: ["./modaldialog.component.scss"],
  host: {
    "(window:resize)": "onWindowResize()",
  },
})
export class ModalDialogComponent {
  @ViewChild("bodyContainer", { read: ViewContainerRef }) bodyContainerRef: ViewContainerRef;

  focusButtonEventEmitter: EventEmitter<boolean>;
  title: string;
  body: string;
  buttons: ModalDialogButton[];
  subject: Subject<ModalDialogButton>;
  classIn: boolean;
  classBlock: boolean;
  classSize: string;
  showCloseButton: boolean;
  modalMaxHeight: number;

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {
    this.focusButtonEventEmitter = new EventEmitter<boolean>();
    this.title = "Title";
    this.body = "Body ...";
    this.buttons = [];
    this.subject = null;
    this.classIn = false;
    this.classBlock = false;
    this.classSize = "";
    this.showCloseButton = true;
    this.onWindowResize();
  }

  showAlert(title: string, body: string): Observable<ModalDialogButton> {
    this.setTitle(title);
    this.setBody(body);
    this.setShowCloseButton(true);
    this.setButtonOk();
    return this.show();
  }

  showConfirmation(title: string, body: string): Observable<ModalDialogButton> {
    this.setTitle(title);
    this.setBody(body);
    this.setShowCloseButton(false);
    this.setButtonNoYes();
    return this.show();
  }

  show(): Observable<ModalDialogButton> {
    this.classBlock = true;
    setTimeout(() => (this.classIn = true));

    if (this.buttons.length > 0) {
      setTimeout(() => {
        this.focusButtonEventEmitter.emit(true);
      });
    }

    this.subject = new Subject<ModalDialogButton>();
    return this.subject.asObservable();
  }

  close() {
    this.classIn = false;
    setTimeout(() => {
      this.classBlock = false;
      this.subject.complete();
    }, 400);
  }

  setTitle(title: string) {
    this.title = title;
  }

  setBody(body: string) {
    this.body = body;
  }

  setBodyComponent<T>(component: Type<T>): T {
    const factory = this.componentFactoryResolver.resolveComponentFactory(component);
    this.setBody("");
    let componentRef = this.bodyContainerRef.createComponent(factory);
    return componentRef.instance;
  }

  setSize(size: MODALDIALOGSIZE): void {
    if (size == MODALDIALOGSIZE.Small) {
      this.classSize = "modal-sm";
    } else if (size == MODALDIALOGSIZE.Normal) {
      this.classSize = "";
    } else if (size == MODALDIALOGSIZE.Large) {
      this.classSize = "modal-lg";
    }
  }

  setShowCloseButton(show: boolean) {
    this.showCloseButton = show;
  }

  setButtonOk() {
    this.buttons = [];

    let button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = "OK";
    button.buttonClass = "primary";
    button.disabled = false;
    this.buttons.push(button);
  }

  setButtonCancel() {
    this.buttons = [];

    let button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = "Cancel";
    button.buttonClass = "default";
    button.disabled = false;
    this.buttons.push(button);
  }

  setButtonCancelOk() {
    this.buttons = [];

    let button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = "Cancel";
    button.buttonClass = "default";
    button.disabled = false;
    this.buttons.push(button);

    button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = "OK";
    button.buttonClass = "primary";
    button.disabled = false;
    this.buttons.push(button);
  }

  setButtonNoYes() {
    this.buttons = [];

    let button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = "No";
    button.buttonClass = "danger";
    button.disabled = false;
    this.buttons.push(button);

    button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = "Yes";
    button.buttonClass = "success";
    button.disabled = false;
    this.buttons.push(button);
  }

  addButton(text: string, buttonClass: string, callback?: () => void) {
    let button = new ModalDialogButton();
    button.index = this.buttons.length;
    button.text = text;
    button.buttonClass = buttonClass;
    button.disabled = false;
    button.callback = callback;
    this.buttons.push(button);
  }

  onClick(button: ModalDialogButton) {
    this.subject.next(button);

    if (button.callback) {
      button.callback();
    }
  }

  onWindowResize(): void {
    const marginTop = 30;
    const header = 56;
    const footer = 65;
    this.modalMaxHeight = window.innerHeight - header - footer - 2 * marginTop;
  }
}

export class ModalDialogButton {
  index: number;
  text: string;
  buttonClass: string;
  callback: any;
  disabled: boolean;
}
